// @flow
import _ from 'lodash';
import isValid from 'date-fns/is_valid';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import getMonth from 'date-fns/get_month';
import getYear from 'date-fns/get_year';
import AddressUtils from './address';

import type { MemberType, ApplicantType } from '../../types/member';
import type { IfpAppType } from '../../types/ifp-app';
import type {
  PlanDateParamsType,
  EligibilityRequestParamsType,
  RecommendationRequestParamsType,
} from '../../types/plan';
import type { HealthPlanType, HealthPlanModsType } from '../../types/health-plan';


export default class PlanUtils {
  // This demos string will be used while the user has yet to give us age/tobacco
  // p27f = primary 27 y/o no tobacco (false)
  static defaultDemos = 'p27f';

  static getDateParams(date: Date = new Date(), type: string = 'health'): PlanDateParamsType {
    const parsedDate = parse(date);
    if (!isValid(parsedDate)) {
      throw new TypeError('[PlanUtils.getDateParams] Invalid Date supplied to getDateParams');
    }

    const nextYearCutoff = type === 'health' ? 9 : 10;
    const buyDate = format(parsedDate, 'YYYY-MM-DD');
    const year = getYear(parsedDate) + (getMonth(parsedDate) > nextYearCutoff ? 1 : 0);

    return { year, buyDate };
  }

  static getMemberParams(members: Array<MemberType>): string {
    return members.map((member: MemberType) => {
      const memberType = member.type || 'dependent';
      const tobacco = member.tobacco ? 't' : 'f';
      const { age } = member;

      let type = memberType.charAt(0);

      if (type === 'd') {
        type = '';
      }

      return `${type}${age}${tobacco}`;
    }).join(',');
  }

  static getAppParams(
    ifpApp: IfpAppType,
    members: Array<MemberType>,
  ): EligibilityRequestParamsType {
    const addressParams = AddressUtils.toRequestGeo(_.get(ifpApp, 'address', {}));
    const subsidy = _.get(ifpApp, 'app_json.subsidy', null);
    const metalTypes = _.get(ifpApp, 'app_json.ede.metalTypes', null);
    const params: EligibilityRequestParamsType = {
      ...addressParams,
      onexchange: (subsidy && subsidy.amount > 0) || 'cross',
      demos: (Array.isArray(members) && PlanUtils.getMemberParams(members)) ||
        PlanUtils.defaultDemos,
    };
    if (_.isArray(metalTypes) && metalTypes.length > 0) {
      params.metalTypes = metalTypes.map(_.toLower).join(',');
    }
    if (subsidy && subsidy.csr) {
      params.csr = subsidy.csr;
    }
    return params;
  }

  static getRecommendationParams(
    ifpApp: IfpAppType,
    applicants: Array<ApplicantType>,
    year: number,
    buyDate?: Date,
  ): RecommendationRequestParamsType {
    const address = _.get(ifpApp, 'address', {});
    const subsidy = _.get(ifpApp, 'app_json.subsidy', null);
    const metalTypes = _.get(ifpApp, 'app_json.ede.metalTypes', null);
    const params: RecommendationRequestParamsType = {
      ...PlanUtils.getDateParams(buyDate),
      year,
      address: AddressUtils.toRequestGeo(address),
      applicants,
      subsidy,
    };

    if (_.isArray(metalTypes) && metalTypes.length > 0) {
      params.metalTypes = metalTypes.map(_.toLower);
    }

    return params;
  }

  static mergePlanDetails(
    planMods: Array<HealthPlanModsType>,
    details: Array<Object>,
    carrierDetails: Array<Object>,
  ): Array<HealthPlanType> {
    const mergedPlans = _.map(details, (detail) => {
      const carrierInfo = _.find(carrierDetails, ['id', detail.carrierId]) || {};
      const carrier = _.get(carrierInfo, 'name');
      return {
        ...detail,
        carrierInfo,
        carrier,
        mods: _.find(planMods, ['planId', detail.id]),
      };
    });
    return PlanUtils.sortPlans(_.reject(mergedPlans, plan => (!plan.mods || !plan.carrier)));
  }

  static sortPlans(plans: Array<HealthPlanType>): Array<HealthPlanType> {
    return _.clone(plans).sort((a, b) => (a.mods.ranks.strideRank - b.mods.ranks.strideRank));
  }

  static formatPlanArguments(plan: HealthPlanType): Object {
    return {
      carrier_id: plan.carrierId,
      master_plan_id: plan.masterId,
      plan,
      plan_id: plan.id,
      premium: _.get(plan, 'mods.premiumMonth'),
    };
  }
}
